import React, { useMemo, useCallback } from "react";

import "./Results.css";
import HeaderElement from "../../components/HeaderElement/HeaderElement";
import VoteType from "../../components/Voting/VotingCard/VoteType/VoteType";
import ResultsCard from "../../components/Voting/ResultsCard/ResultsCard";
import BarChartIcon from "@mui/icons-material/BarChart";
import copyIcon from "../../assets/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { useApi } from "../../hooks/useApi";
import { useNavigate } from 'react-router-dom'
import { useUI } from "../../hooks/usePermission";
import StandardPopup from "../../components/Global/StandardPopup/StandardPopup";
import popupicon from "../../assets/icons/popup.svg";
import strings from "../../locales/ro.json";


const Results = ({ ballot }) => {
  const navigate = useNavigate();

  const [optionVotes, setOptionVotes] = React.useState([]);
  const [nrQualifiedVotes, setNrQualifiedVotes] = React.useState(0);
  const [expressedVotes, setExpressedVotes] = React.useState(0);
  const [totalVoters, setTotalVoters] = React.useState(0);
  const [openInvalidationPopup, setOpenInvalidationPopup] = React.useState(false);
  const { api } = useApi();
  const canManageBallots = useUI('action.ballots.manage');

  // Memoize the copy text generation
  const copyText = useMemo(() => {
    if (!ballot || !optionVotes.length) return '';

    return `Title: ${ballot.title} - Created At: ${ballot.createdAt} - Closed At: ${ballot.closedAt} \n${ballot.question.question_text
      } \nVot ${ballot.showVotesPublicly ? "deschis" : "inchis"} \n${optionVotes.map(option =>
        `${option.name} : ${option.vote_count} ${ballot.showVotesPublicly && option.voters?.length ?
          "(" + option.voters.join(", ") + ")" : ""
        }`
      ).join('\n')
      }`;
  }, [ballot, optionVotes]);

  const copyResultsToClipboard = useCallback(() => {
    navigator.clipboard.writeText(copyText);
  }, [copyText]);


  const archiveBallot = useCallback(async () => {
    try {
      const requests = [api(`ballot/${ballot.question.ballot_id}`, "DELETE")];

      if (ballot?.juniorBallotId) {
        requests.push(
          api(`ballot/${ballot.juniorBallotId}`, "DELETE")
        );
      }

      if (ballot?.alumniBallotId) {
        requests.push(
          api(`ballot/${ballot.alumniBallotId}`, "DELETE")
        );
      }

      await Promise.all(requests);
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error archiving ballot:", error);
    }
  }, [ballot, api, navigate]);

  React.useEffect(() => {
    if (!ballot) return;

    const fetchData = async () => {
      try {
        let totalVotersCount = ballot.question.voters.length;
        let options = [...ballot.question.options];

        // Handle split vote case — fetch children for voter count
        if (ballot.juniorBallotId && ballot.alumniBallotId) {
          const [juniorBallot, alumniBallot] = await Promise.all([
            api(`ballot/${ballot.juniorBallotId}`, "GET"),
            api(`ballot/${ballot.alumniBallotId}`, "GET")
          ]);

          totalVotersCount += juniorBallot.question.voters.length + alumniBallot.question.voters.length;
          if (juniorBallot.question.voters.length) totalVotersCount -= 1;
          if (alumniBallot.question.voters.length) totalVotersCount -= 1;
        }

        // For public votes, enrich options with voter names
        if (ballot.showVotesPublicly) {
          const usersData = await api("users", "GET");

          const usersMap = usersData.reduce((acc, u) => {
            acc[u.id] = u.name;
            return acc;
          }, {});

          const voteMap = ballot.votes.reduce((acc, vote) => {
            if (!acc[vote.option_id]) acc[vote.option_id] = [];
            acc[vote.option_id].push(usersMap[vote.user_id] || "Unknown User");
            return acc;
          }, {});

          options = options.map(option => ({
            ...option,
            voters: voteMap[option.id] || []
          }));
        }

        const expressedVotesTmp = options.reduce((sum, option, index) =>
          index === options.length - 1 ? sum : sum + option.vote_count, 0);

        setTotalVoters(totalVotersCount);
        setNrQualifiedVotes(ballot.question.voters.length);
        setExpressedVotes(expressedVotesTmp);
        setOptionVotes(options);
      } catch (error) {
        console.error('Error fetching ballot data:', error);
      }
    };

    fetchData();
  }, [ballot, api]);

  const abstainOption = useMemo(() =>
    optionVotes[optionVotes.length - 1],
    [optionVotes]
  );

  const getOtherVotes = useCallback((currentOption) => {
    if (!optionVotes.length) return [];
    return optionVotes
      .filter(option => option !== currentOption && option !== abstainOption)
      .map(option => option.vote_count);
  }, [optionVotes, abstainOption]);

  return (
    <div>
      <div className="resultsPageContainer">
        {ballot && (
          <div>
            <HeaderElement headerText={ballot.title} />
            <div className="resultsPageBodyContainer">
              <div className="metadataContainer">
                {ballot.isGeneralAssemblySplitVote && (
                  <>
                    <div className="ballotTitle">
                      {ballot.question.question_text}
                    </div>
                    <div className="resultsPageVoteCount">
                      Total voturi: {nrQualifiedVotes}
                    </div>
                  </>
                )}
                {!ballot.isGeneralAssemblySplitVote && (
                  <div className="ballotTitle">
                    {ballot.question.question_text}
                  </div>
                )}
                <div className="resultsPageVoteCount">
                  Total votanti: {totalVoters}
                </div>
                <VoteType voteType={ballot.showVotesPublicly} />
              </div>
              <div className="resultsPageOptionsContainer">
                {optionVotes.map((option, index) => (
                  <ResultsCard
                    key={option.id || index}
                    total={option !== abstainOption ? expressedVotes : nrQualifiedVotes}
                    name={option.name}
                    voteCount={option.vote_count}
                    countingRule={ballot.question.vote_couting_rule}
                    otherVotes={getOtherVotes(option)}
                    type={ballot.showVotesPublicly ? "public" : "private"}
                    isAbstain={index === optionVotes.length - 1 && ballot.isGeneralAssemblySplitVote}
                  >
                    {ballot.showVotesPublicly && option.voters.map((name, index2) => (
                      <div key={`${option.id}-${index2}`}>{name}</div>
                    ))}
                  </ResultsCard>
                ))}
              </div>
              <div className="resultsButtonsContainer">
                {ballot.parentBallotId === null && ballot.showVotesPublicly === 1 && ballot.isGeneralAssemblySplitVote && (
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.juniorBallotId}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Votul Juniorilor</div>
                  </button>
                )}
                {ballot.parentBallotId === null && ballot.showVotesPublicly === 1 && ballot.isGeneralAssemblySplitVote && (
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.alumniBallotId}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Votul Alumnilor</div>
                  </button>
                )}
                {ballot.parentBallotId !== null && ballot.showVotesPublicly === 1 && (
                  <button
                    className="voteStatusButton"
                    onClick={() => {
                      window.location.replace(`/ballot/${ballot.parentBallotId}`)
                    }}
                  >
                    <BarChartIcon />
                    <div>Vezi Rezultatul Final</div>
                  </button>
                )}
                {canManageBallots && (
                  <button
                    className="voteStatusButton"
                    onClick={() => setOpenInvalidationPopup(true)}
                  >
                    <BarChartIcon />
                    <div>Arhivează Votul</div>
                  </button>
                )}
                <button
                  className="copyResultsButton"
                  data-tooltip-id="copyTooltip"
                  onClick={copyResultsToClipboard}
                >
                  <img src={copyIcon} alt="copy" />
                  <span>Copiază Rezultatul</span>
                </button>
              </div>
            </div>
            <StandardPopup
              message={strings.editvote.invalidatePopup}
              isOpen={openInvalidationPopup}
              yesFcn={archiveBallot}
              noFcn={() => setOpenInvalidationPopup(false)}
              icon={popupicon}
            />
          </div>
        )}
        <Tooltip id="copyTooltip" content="Copiat!" events={["click"]} />
      </div>
    </div>
  );
};

export default React.memo(Results);
